import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { NgIf } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';

import { NegativeLabelPipe } from '../../../pipes/negative-label.pipe';
import {
  FILING_FORM_ROUTE,
  FIELD_LINK_SCROLL_PARAM,
  fieldLinkQueryParams,
  type FieldLinkUi,
} from '../../../utils/field-link-navigation.util';
import { fieldLinkTooltipModel } from '../../../utils/field-link-label.util';
import { formatFieldLinkTooltip } from '../../../utils/field-link-tooltip.model';
import {
  inboundLinksFor,
  type InboundFieldLink,
} from '../../../utils/field-link-reverse-index.util';
import type { SheetType } from '../../../models/sheet-types';
import { FieldLinkBackChooserComponent } from '../field-link-back-chooser/field-link-back-chooser.component';

/** The slice of a schema control this component needs. */
export interface FieldLinkLabelControl {
  qname: string;
  label: string;
  ui: FieldLinkUi;
}

/**
 * A filing-sheet row label — an anchor when the row links somewhere, a plain
 * span when it does not. **One label for every sheet.**
 *
 * Replaces seven copies of the same `*ngIf="linkQueryParams(control) as qp; else
 * plainLabel"` block and fourteen delegate methods. A control with no resolvable
 * link renders a plain `<span>`: no dead anchor, no tooltip.
 *
 * ## Usage — the marker is NOT part of this component
 *
 * ```html
 * <app-field-link-label [control]="control" /><app-required-marker [when]="isFieldRequired(control)" />
 * ```
 *
 * The marker's `[when]` needs sheet-specific context this component cannot know,
 * so it stays in the sheet template — glued with NO whitespace and no source
 * newline, or the browser gets a line-break opportunity and drops a lone `*` onto
 * the next line. Both hosts are `inline`, so gluing works exactly as before.
 *
 * ## Two things not to change
 *
 * - **`[escape]="true"` on the tooltip.** Schema labels carry markup — the `(-)`
 *   prefixes and the `negativeLabel` pipe's output — so an unescaped tooltip is
 *   an HTML-injection seam for a feature that only needs line breaks. Multi-line
 *   comes from `white-space: pre-line` on `.field-link-tooltip`, not from HTML.
 * - **Back-links are opt-in and currently unreachable.** `inboundLinksFor`
 *   filters on `ui.linkTo.bidirectional`, which no schema sets, so `backLinks()`
 *   is always empty today. That is the P.O.'s instruction, not an oversight.
 *
 * ## Developer Note — KNOWN ACCESSIBILITY REGRESSION, revisit before any
 * ## screen-reader commitment
 *
 * This component renders its tooltip through PrimeNG's `pTooltip`, replacing the
 * native `title` attribute the seven sheets used before consolidation. PrimeNG's
 * tooltip emits **no `aria-describedby`** and is **hover/focus-driven only**, so
 * the copy `Open "Total revenue" in Analysis of Profit or Loss` is no longer
 * exposed to assistive technology the way a native `title` was.
 *
 * What is NOT lost: the link itself. The anchor keeps its `routerLink`, its
 * visible text and its place in the tab order, so a keyboard or screen-reader
 * user can still reach and follow every field link. Only the supplementary hint
 * naming the DESTINATION field and sheet is unavailable — which matters here
 * because the destination label deliberately differs from the source label (the
 * Statement of Financial Position's "Borrowings" leads to "Total non-current
 * borrowings", and that sheet has two "Borrowings" rows).
 *
 * The trade was made knowingly: `title` cannot render the multi-line, escaped
 * content the tooltip model is built for. **If the P.O. ever commits to
 * supporting screen-reader users, this is the first thing to revisit.** The fix
 * is small and local — bind `[attr.aria-describedby]` to a visually-hidden
 * element carrying `tooltipText()`, or add an `[attr.aria-label]` composed from
 * the same model — but it must land here, in this one component, and nowhere
 * else. See `docs/ui/field-links/README.md`.
 */
@Component({
  selector: 'app-field-link-label',
  standalone: true,
  imports: [NgIf, RouterLink, TooltipModule, NegativeLabelPipe, FieldLinkBackChooserComponent],
  templateUrl: './field-link-label.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host { display: inline; }
      ::ng-deep .field-link-tooltip .p-tooltip-text { white-space: pre-line; }
    `,
  ],
})
export class FieldLinkLabelComponent {
  @Input({ required: true }) control!: FieldLinkLabelControl;

  /** This row's OWN sheet — needed only to look up back-links. */
  @Input() destinationSheet?: SheetType;

  /** Active taxonomy, so the benign MFRS/MPERS pair is not read as a collision. */
  @Input() taxonomy?: 'mfrs' | 'mpers';

  protected readonly FILING_FORM_ROUTE = [...FILING_FORM_ROUTE];
  protected chooserVisible = false;

  /** Forward-link queryParams, or null when this row links nowhere. */
  forwardParams(): Record<string, string> | null {
    return fieldLinkQueryParams(this.control.ui, this.control.qname) as Record<string, string> | null;
  }

  /** Flattened tooltip copy for the forward link. */
  tooltipText(): string | undefined {
    // `undefined`, never `null`: PrimeNG's pTooltip accepts
    // `string | TemplateRef | undefined`, so a null would fail strictTemplates.
    return (
      formatFieldLinkTooltip(
        fieldLinkTooltipModel(this.control.ui, this.control.qname, this.control.label)
      ) ?? undefined
    );
  }

  /** Opted-in inbound links. Empty for every row today — by design. */
  backLinks(): InboundFieldLink[] {
    if (!this.destinationSheet) return [];
    return inboundLinksFor(this.destinationSheet, this.control.qname, this.taxonomy);
  }

  /** More than one candidate means the user must choose. */
  shouldUseChooser(): boolean {
    return this.backLinks().length > 1;
  }

  /** Single-candidate back-link params; null when the chooser handles it. */
  backParams(): Record<string, string> | null {
    const links = this.backLinks();
    if (links.length !== 1) return null;
    return { sheet: links[0].sourceSheet, [FIELD_LINK_SCROLL_PARAM]: links[0].sourceQname };
  }

  backTooltipText(): string | undefined {
    // `undefined` rather than `null` — see tooltipText().
    const links = this.backLinks();
    if (links.length === 0) return undefined;
    if (links.length === 1)
      return `Open "${links[0].sourceLabel}" in ${links[0].sourceSheetName}`;
    return `This value is shared by ${links.length} fields — choose where to go`;
  }

  onBackClick(): void {
    if (this.shouldUseChooser()) this.chooserVisible = true;
  }
}
